import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, Minus, Plus, Loader2, Truck, Boxes, Trash2, FileDown, CheckCircle2 } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { INVENTORY_ROOMS, TRUCK_BANDS, truckForVolume } from "@/data/inventoryData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full rounded-lg border border-line bg-white px-3 py-3 text-sm text-slate-900 outline-none focus:border-blaze";

const Stepper = ({ value, onChange, testid }) => (
  <div className="flex items-center gap-1.5">
    <button type="button" onClick={() => onChange(Math.max(0, value - 1))} className="flex h-8 w-8 items-center justify-center rounded-full border border-line text-slate-600 active:scale-90 disabled:opacity-30" disabled={value === 0} data-testid={`${testid}-minus`}>
      <Minus className="h-3.5 w-3.5" />
    </button>
    <span className={`w-7 text-center text-sm font-bold ${value > 0 ? "text-blaze" : "text-slate-400"}`} data-testid={`${testid}-qty`}>{value}</span>
    <button type="button" onClick={() => onChange(value + 1)} className="flex h-8 w-8 items-center justify-center rounded-full border border-blaze bg-blue-50 text-blaze active:scale-90" data-testid={`${testid}-plus`}>
      <Plus className="h-3.5 w-3.5" />
    </button>
  </div>
);

export default function InventoryCalculator() {
  const [qty, setQty] = useState({});
  const [openRoom, setOpenRoom] = useState(INVENTORY_ROOMS[0].room);
  const [custom, setCustom] = useState([]);
  const [cForm, setCForm] = useState({ name: "", volume: "", qty: 1 });
  const [form, setForm] = useState({ name: "", email: "", phone: "", pickup_suburb: "", drop_suburb: "", moving_date: "" });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const selected = useMemo(() => {
    const list = [];
    INVENTORY_ROOMS.forEach(({ room, items }) => {
      items.forEach(([name, volume]) => {
        const q = qty[`${room}|${name}`] || 0;
        if (q > 0) list.push({ room, name, volume, qty: q });
      });
    });
    custom.forEach((c) => list.push({ room: "Custom Items", name: c.name, volume: c.volume, qty: c.qty }));
    return list;
  }, [qty, custom]);

  const totalVolume = useMemo(() => Math.round(selected.reduce((s, i) => s + i.qty * i.volume, 0) * 100) / 100, [selected]);
  const truck = truckForVolume(totalVolume);

  const addCustom = () => {
    const v = parseFloat(cForm.volume);
    if (!cForm.name.trim() || !Number.isFinite(v) || v <= 0) return toast.error("Enter the item name and its volume in m³.");
    setCustom((c) => [...c, { name: cForm.name.trim(), volume: v, qty: Math.max(1, Number(cForm.qty) || 1) }]);
    setCForm({ name: "", volume: "", qty: 1 });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (selected.length === 0) return toast.error("Select at least one item first.");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/inventory-quote`, { ...form, items: selected });
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send your quote request. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (result) {
    return (
      <main className="min-h-screen">
        <Header />
        <div className="mx-auto max-w-2xl px-5 pb-24 pt-36 text-center" data-testid="inventory-success">
          <CheckCircle2 className="mx-auto h-16 w-16 text-green-600" />
          <h1 className="mt-4 font-display text-3xl font-bold uppercase tracking-tight text-slate-900">Quote Request <span className="text-blaze">Sent!</span></h1>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">
            Thanks {form.name.split(" ")[0]}! We've emailed you a copy with your full inventory PDF. Our team will get back to you shortly with your quote.
          </p>
          <div className="mx-auto mt-6 grid max-w-sm grid-cols-2 gap-3 rounded-2xl border border-line bg-white p-5">
            <div><p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Total Volume</p><p className="font-display text-2xl font-bold text-blaze">{result.total_volume} m³</p></div>
            <div><p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Recommended</p><p className="font-display text-lg font-bold text-slate-900">{result.truck}</p></div>
          </div>
          <a href={`${API}/inventory-quote/${result.id}/pdf`} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 rounded-lg bg-blaze px-6 py-3.5 text-xs font-bold uppercase tracking-widest text-white" data-testid="inventory-pdf-link">
            <FileDown className="h-4 w-4" /> Download Inventory PDF
          </a>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main className="min-h-screen">
      <Header />
      <h1 className="sr-only">MoversNearby Inventory Calculator — Estimate Your Moving Volume</h1>
      <div className="mx-auto max-w-4xl px-4 pb-24 pt-28 lg:pt-36" data-testid="inventory-calculator">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-blaze">MoversNearby</p>
        <h2 className="mt-1 font-display text-3xl font-bold uppercase tracking-tight text-slate-900 sm:text-4xl">Inventory <span className="text-blaze">Calculator</span></h2>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-600">
          <strong>How it works:</strong> select the items you are moving, enter the quantity, and our calculator will estimate your total moving volume in cubic metres (m³) and recommend the right truck.
        </p>

        <div className="sticky top-16 z-30 mt-6 flex items-center justify-between gap-3 rounded-2xl border border-line bg-white/95 p-4 shadow-lg backdrop-blur lg:top-24" data-testid="inventory-summary-bar">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-50"><Boxes className="h-5 w-5 text-blaze" /></span>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Estimated Volume</p>
              <p className="font-display text-xl font-bold text-blaze" data-testid="total-volume">{totalVolume.toFixed(2)} m³</p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-right">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Recommended Truck</p>
              <p className="font-display text-sm font-bold text-slate-900 sm:text-base" data-testid="recommended-truck">{truck}</p>
            </div>
            <span className="hidden h-11 w-11 items-center justify-center rounded-full bg-green-50 sm:flex"><Truck className="h-5 w-5 text-green-600" /></span>
          </div>
        </div>

        <div className="mt-6 space-y-3">
          {INVENTORY_ROOMS.map(({ room, items }) => {
            const count = items.reduce((s, [n]) => s + (qty[`${room}|${n}`] || 0), 0);
            const isOpen = openRoom === room;
            return (
              <div key={room} className="overflow-hidden rounded-2xl border border-line bg-white" data-testid={`room-${room.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <button type="button" onClick={() => setOpenRoom(isOpen ? "" : room)} className="flex w-full items-center justify-between px-4 py-4 text-left">
                  <span className="font-display text-sm font-bold uppercase tracking-wide text-slate-900">
                    {room}
                    {count > 0 && <span className="ml-2 rounded-full bg-blaze px-2 py-0.5 text-[10px] font-bold text-white">{count}</span>}
                  </span>
                  <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="divide-y divide-line/60 border-t border-line">
                    {items.map(([name, volume]) => {
                      const key = `${room}|${name}`;
                      return (
                        <div key={key} className="flex items-center justify-between gap-3 px-4 py-2.5">
                          <div className="min-w-0">
                            <p className={`truncate text-sm ${qty[key] ? "font-semibold text-slate-900" : "text-slate-700"}`}>{name}</p>
                            <p className="text-[11px] text-slate-400">{volume.toFixed(2)} m³</p>
                          </div>
                          <Stepper value={qty[key] || 0} onChange={(v) => setQty((q) => ({ ...q, [key]: v }))} testid={`item-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          <div className="rounded-2xl border border-dashed border-blaze/50 bg-blue-50/50 p-4" data-testid="custom-item-section">
            <p className="font-display text-sm font-bold uppercase tracking-wide text-slate-900">Item not listed? Add a custom item</p>
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
              <input className={`${inputCls} col-span-2 sm:col-span-2`} placeholder="Item name" value={cForm.name} onChange={(e) => setCForm({ ...cForm, name: e.target.value })} data-testid="custom-name" />
              <input type="number" min="0.01" step="0.05" className={inputCls} placeholder="Volume m³" value={cForm.volume} onChange={(e) => setCForm({ ...cForm, volume: e.target.value })} data-testid="custom-volume" />
              <div className="flex items-center justify-between gap-2">
                <Stepper value={Number(cForm.qty)} onChange={(v) => setCForm({ ...cForm, qty: Math.max(1, v) })} testid="custom" />
                <button type="button" onClick={addCustom} className="rounded-lg bg-blaze px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white active:scale-95" data-testid="custom-add-btn">Add</button>
              </div>
            </div>
            {custom.length > 0 && (
              <div className="mt-3 space-y-1.5">
                {custom.map((c, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg bg-white px-3 py-2 text-sm" data-testid={`custom-item-${i}`}>
                    <span className="font-semibold text-slate-900">{c.name} <span className="text-xs font-normal text-slate-500">× {c.qty} · {c.volume} m³ each</span></span>
                    <button type="button" onClick={() => setCustom(custom.filter((_, j) => j !== i))} className="text-red-500" data-testid={`custom-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 rounded-2xl border border-line bg-white p-5" data-testid="truck-bands">
          <p className="font-display text-sm font-bold uppercase tracking-wide text-slate-900">Recommended Truck Guide</p>
          <div className="mt-3 grid gap-2 text-xs text-slate-600 sm:grid-cols-2">
            {TRUCK_BANDS.map((b) => (
              <p key={b.label} className={`rounded-lg px-3 py-2 ${truck === b.label ? "bg-blue-50 font-bold text-blaze" : "bg-slate-50"}`}>{b.range}: {b.label}</p>
            ))}
            <p className={`rounded-lg px-3 py-2 sm:col-span-2 ${truck.startsWith("Multiple") ? "bg-blue-50 font-bold text-blaze" : "bg-slate-50"}`}>Over 65 m³: Multiple Trucks / Special Assessment Required</p>
          </div>
        </div>

        <form onSubmit={submit} className="mt-8 rounded-2xl border-2 border-blaze bg-white p-5 sm:p-6" data-testid="inventory-quote-form">
          <h3 className="font-display text-xl font-bold uppercase tracking-tight text-slate-900">Get My <span className="text-blaze">Free Quote</span></h3>
          <p className="mt-1 text-xs text-slate-500">We'll email you a copy of your inventory (PDF) with the estimated volume and recommended truck.</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <input required className={inputCls} placeholder="Moving From (suburb) *" value={form.pickup_suburb} onChange={(e) => setForm({ ...form, pickup_suburb: e.target.value })} data-testid="iq-pickup" />
            <input required className={inputCls} placeholder="Moving To (suburb) *" value={form.drop_suburb} onChange={(e) => setForm({ ...form, drop_suburb: e.target.value })} data-testid="iq-drop" />
            <input required className={inputCls} placeholder="Your Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="iq-name" />
            <input required type="tel" className={inputCls} placeholder="Phone *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="iq-phone" />
            <input required type="email" className={inputCls} placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="iq-email" />
            <input type="date" className={inputCls} value={form.moving_date} onChange={(e) => setForm({ ...form, moving_date: e.target.value })} data-testid="iq-date" />
          </div>
          <button type="submit" disabled={sending || selected.length === 0} className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-blaze py-4 text-sm font-bold uppercase tracking-[0.15em] text-white active:scale-[0.99] disabled:opacity-50" data-testid="iq-submit">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Get My Free Quote — {totalVolume.toFixed(2)} m³
          </button>
        </form>
      </div>
      <Footer />
    </main>
  );
}
